import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@glplus/glpweb-dsl';
import { Panel } from './Panel';
import { DayPicker } from './DayPicker';
import { runnerApi, RunnerApiError, asDeliveryConflict, type DeliveryConflict, type TimeOff } from '../lib/runnerApiClient';

/**
 * §6 time off — the override on top of recurring availability, so it lives on
 * the schedule page rather than its own nav item. A Runner asking "when am I
 * working" wants both answers together.
 *
 * 🔑 The copy never asks a Runner to justify or categorise an absence. Reason
 * is optional free text, deliberately not a vacation/sick dropdown: pushing a
 * contractor to declare illness collects health information we have no business
 * holding. The placeholder suggests a neutral example for that reason.
 *
 * 🔑 Short notice is shown as a fact about customer notice, never as a warning
 * aimed at the Runner. The API accepts same-day absences on purpose — refusing
 * them would produce a no-show instead, which is worse for the customer the
 * notice rule exists to protect. The UI must not undo that with scolding copy.
 */

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Dates render in the viewer's locale rather than a hand-rolled format. */
function prettyDate(ymd: string, locale: string): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d);
}

/** Past absences are history; anything still running or ahead is current. */
function isPast(t: TimeOff): boolean {
  return t.endsOn < todayYmd();
}

const STATUS_TONE: Record<string, string> = {
  requested: '#B45309',
  approved: '#2E7D32',
  denied: '#b42318',
  cancelled: '#5f6b7a',
};

export function TimeOffPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const [rows, setRows] = useState<TimeOff[] | null>(null);
  const [noticeTarget, setNoticeTarget] = useState(7);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [reason, setReason] = useState('');
  /**
   * The deliveries these days would stand up. Held rather than thrown away so
   * the Runner can read the list and then proceed — the API refuses once, and
   * this is the screen that earns the second attempt.
   */
  const [conflict, setConflict] = useState<DeliveryConflict | null>(null);
  /**
   * The block a Runner has asked to undo, held until they confirm.
   *
   * 🛑 Cancelling is NOT symmetric with booking, and the prompt exists to say
   * so: blocking the days cancelled the deliveries in them, and putting the
   * days back does not un-cancel anything. A Runner who assumes it does will
   * believe they are still on the hook for a run somebody else now has.
   */
  const [undoing, setUndoing] = useState<TimeOff | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await runnerApi.timeOff();
      setRows(d.timeOff);
      setNoticeTarget(d.noticeTargetDays);
    } catch (e) {
      setRows([]);
      setError(e instanceof RunnerApiError ? e.message : t('timeOff.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const upcoming = useMemo(
    () => (rows ?? []).filter((r) => !isPast(r) && r.status !== 'cancelled' && r.status !== 'denied'),
    [rows],
  );
  const closed = useMemo(
    () => (rows ?? []).filter((r) => isPast(r) || r.status === 'cancelled' || r.status === 'denied'),
    [rows],
  );

  function reset() {
    setStartsOn('');
    setEndsOn('');
    setReason('');
    setAdding(false);
    setError(null);
  }

  async function submit(acknowledge = false) {
    setBusy(true);
    setError(null);
    try {
      await runnerApi.createTimeOff({
        startsOn,
        // A single day off is the common case, so an empty end date means
        // "just that day" rather than an error the Runner has to decode.
        endsOn: endsOn || startsOn,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(acknowledge ? { acknowledgeDeliveries: true } : {}),
      });
      setConflict(null);
      reset();
      await load();
    } catch (e) {
      const c = asDeliveryConflict(e);
      // Not an error to show in red — it is the confirmation step.
      if (c) { setConflict(c); return; }
      // The 409 overlap message names the dates it collides with and is written
      // to be read by a Runner — surface it verbatim.
      setError(e instanceof RunnerApiError ? e.message : t('timeOff.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(row: TimeOff) {
    setBusy(true);
    setError(null);
    try {
      await runnerApi.cancelTimeOff(row.id);
      setUndoing(null);
      await load();
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('timeOff.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (rows === null) return null;

  return (
    <Panel title={t('timeOff.title')} accent="#B45309" dataTestId="timeoff-panel">
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2 }}>
        {t('timeOff.subtitle', { days: noticeTarget })}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" data-testid="timeoff-error">{error}</Alert>
        </Box>
      )}

      {upcoming.length === 0 ? (
        <Box data-testid="timeoff-empty" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 1.75, py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{t('timeOff.emptyTitle')}</Typography>
          <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 0.5 }}>{t('timeOff.empty')}</Typography>
          <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 0.75 }}>
            {t('timeOff.emptyNotice', { days: noticeTarget })}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {upcoming.map((r) => (
            <Box
              key={r.id}
              data-testid={`timeoff-row-${r.id}`}
              data-status={r.status}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                px: 1.75,
                py: 1.25,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>
                  {r.startsOn === r.endsOn
                    ? prettyDate(r.startsOn, locale)
                    : `${prettyDate(r.startsOn, locale)} – ${prettyDate(r.endsOn, locale)}`}
                </Typography>
                <Typography data-testid={`timeoff-status-${r.id}`} sx={{ fontSize: '13px', color: STATUS_TONE[r.status] ?? '#5f6b7a' }}>
                  {t(`timeOff.status.${r.status}`)}
                  {r.reason ? ` · ${r.reason}` : ''}
                </Typography>
                {r.shortNotice && (
                  // 🔒 Rod, 2026-09-12: "short notice is a mark against them."
                  // This used to be worded as a neutral statement about customer
                  // notice and deliberately not a warning at the Runner — that
                  // framing is overruled. Short notice is recorded the same way a
                  // missed delivery is, so the line says so and carries the weight
                  // colour rather than the muted one.
                  // 🛑 Still true, and not in tension with the above: the API
                  // ACCEPTS same-day absences on purpose, because refusing one
                  // produces a no-show, which is worse for the household. We
                  // record it; we never block it.
                  <Typography data-testid={`timeoff-short-notice-${r.id}`} sx={{ fontSize: '12px', color: '#B45309', mt: 0.25, fontWeight: 600 }}>
                    {t('timeOff.shortNotice')}
                  </Typography>
                )}
              </Box>
              <Button
                ariaLabel={t('timeOff.cancel')}
                dataTestId={`timeoff-cancel-${r.id}`}
                buttonText={t('timeOff.cancel')}
                variant="text"
                size="small"
                onClick={() => setUndoing(r)}
                disabled={busy}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {!adding ? (
          <Button
            ariaLabel={t('timeOff.add')}
            dataTestId="timeoff-add"
            buttonText={t('timeOff.add')}
            variant="contained"
            size="small"
            onClick={() => setAdding(true)}
            disabled={busy}
          />
        ) : (
          <>
            {/* DSL >= 3.0.1 renders TextField under a single root, so fields can
                be direct grid children — no Box wrapper needed. */}
            {/* 🔑 A grid, not two date inputs. The Runner needs to see which
                days already carry deliveries BEFORE choosing them — a typed
                date hides that until the save is refused. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 2, alignItems: 'start' }}>
              <DayPicker
                startsOn={startsOn}
                endsOn={endsOn}
                disabled={busy}
                onChange={(from, to) => { setStartsOn(from); setEndsOn(to); }}
              />
              <TextField
                label={t('timeOff.reasonLabel')}
                ariaLabel={t('timeOff.reasonLabel')}
                dataTestId="timeoff-reason"
                size="small"
                fullWidth
                value={reason}
                disabled={busy}
                placeholder={t('timeOff.reasonPlaceholder')}
                onChange={(e) => setReason(e.target.value)}
              />
            </Box>
            <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1 }}>{t('timeOff.hint')}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
              <Button
                ariaLabel={t('timeOff.save')}
                dataTestId="timeoff-save"
                buttonText={t('timeOff.save')}
                variant="contained"
                size="small"
                onClick={() => void submit()}
                disabled={busy || !startsOn}
              />
              <Button
                ariaLabel={t('timeOff.discard')}
                dataTestId="timeoff-discard"
                buttonText={t('timeOff.discard')}
                variant="text"
                size="small"
                onClick={reset}
                disabled={busy}
              />
            </Box>
          </>
        )}
      </Box>

      {closed.length > 0 && (
        <Box data-testid="timeoff-past" sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #e5e7eb' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#5f6b7a', mb: 0.75 }}>
            {t('timeOff.pastTitle')}
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            {closed.map((r) => (
              <Typography key={r.id} data-testid={`timeoff-past-${r.id}`} data-status={r.status} sx={{ color: '#5f6b7a', fontSize: '13px' }}>
                {r.startsOn === r.endsOn
                  ? prettyDate(r.startsOn, locale)
                  : `${prettyDate(r.startsOn, locale)} – ${prettyDate(r.endsOn, locale)}`}{' '}
                · {t(`timeOff.status.${r.status}`)}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* 🔒 The confirmation, not a warning. Rod ruled a Runner always gets to
          take the days — this exists so nobody takes them blind, and the button
          is danger red because it lands on their record. */}
      <Dialog open={conflict !== null} onClose={() => !busy && setConflict(null)} ariaLabelledBy="time-off-conflict" maxWidth="sm" fullWidth>
        <DialogTitle id="time-off-conflict">
          {t('timeOff.conflict.title', { count: conflict?.totalDeliveries ?? 0 })}
        </DialogTitle>
        <DialogContent>
          <Typography data-testid="timeoff-conflict-body" sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>{t('timeOff.conflict.body')}</Typography>
          {(conflict?.affected ?? []).map((a) => (
            <Box key={a.runId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.25, borderBottom: '1px solid #eef2f6' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1f2937' }}>
                  {prettyDate(a.runDate, locale)} · {a.deliveryWindowStart}–{a.deliveryWindowEnd}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#5f6b7a' }}>
                  {t('timeOff.conflict.orders', { count: a.orderCount })}{a.zone ? ` \u00b7 ${a.zone}` : ''}
                </Typography>
              </Box>
              {a.shortNotice && (
                <Typography data-testid="timeoff-conflict-short-notice" sx={{ fontSize: '10px', fontWeight: 700, color: '#B3261E', backgroundColor: '#FCEDEC', borderRadius: 999, px: 1.25, py: 0.5, whiteSpace: 'nowrap' }}>
                  {t('timeOff.conflict.shortNoticePill')}
                </Typography>
              )}
            </Box>
          ))}
          <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, backgroundColor: '#FDF3E1' }}>
            <Typography sx={{ fontSize: '12px', color: '#7A4A08' }}>{t('timeOff.conflict.consequence1')}</Typography>
            {(conflict?.shortNoticeCount ?? 0) > 0 && (
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#7A4A08', mt: 0.5 }}>
                {t('timeOff.conflict.consequence2', { count: conflict?.shortNoticeCount ?? 0 })}
              </Typography>
            )}
            <Typography sx={{ fontSize: '12px', color: '#7A4A08', mt: 0.5 }}>{t('timeOff.conflict.consequence3')}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button ariaLabel={t('timeOff.conflict.back')} dataTestId="timeoff-conflict-back" buttonText={t('timeOff.conflict.back')} variant="outlined" size="small" disabled={busy} onClick={() => setConflict(null)} />
          <Button
            ariaLabel={t('timeOff.conflict.confirm')}
            dataTestId="timeoff-conflict-confirm"
            buttonText={busy ? t('timeOff.save') : t('timeOff.conflict.confirm')}
            variant="contained"
            size="small"
            disabled={busy}
            // 🔒 Rod, 2026-09-12: red, and with the warning glyph. This is the
            // one control in the portal that stands a household up, so it is
            // deliberately not the same button as every other confirmation.
            startIcon="warningAmber"
            iconColor="white"
            sx={{ backgroundColor: '#B3261E', '&:hover': { backgroundColor: '#8F1E18' } }}
            onClick={() => void submit(true)}
          />
        </DialogActions>
      </Dialog>

      {/* 🛑 The undo prompt. It is not "are you sure" theatre — it names the
          one thing a Runner cannot get back. */}
      <Dialog open={undoing !== null} onClose={() => !busy && setUndoing(null)} ariaLabelledBy="time-off-undo" maxWidth="sm" fullWidth>
        <DialogTitle id="time-off-undo">
          {t('timeOff.undo.title', {
            range: undoing
              ? undoing.startsOn === undoing.endsOn
                ? prettyDate(undoing.startsOn, locale)
                : `${prettyDate(undoing.startsOn, locale)} – ${prettyDate(undoing.endsOn, locale)}`
              : '',
          })}
        </DialogTitle>
        <DialogContent>
          <Typography data-testid="timeoff-undo-body" sx={{ color: '#5f6b7a', fontSize: '13px' }}>{t('timeOff.undo.body')}</Typography>
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#FDF3E1' }}>
            <Typography sx={{ fontSize: '13px', color: '#7A4A08', fontWeight: 700 }}>
              {t('timeOff.undo.notBackTitle')}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#7A4A08', mt: 0.5 }}>
              {t('timeOff.undo.notBackBody')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            ariaLabel={t('timeOff.undo.keep')}
            dataTestId="timeoff-undo-keep"
            buttonText={t('timeOff.undo.keep')}
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => setUndoing(null)}
          />
          <Button
            ariaLabel={t('timeOff.undo.confirm')}
            dataTestId="timeoff-undo-confirm"
            buttonText={t('timeOff.undo.confirm')}
            variant="contained"
            size="small"
            disabled={busy}
            startIcon="warningAmber"
            iconColor="white"
            sx={{ backgroundColor: '#B3261E', '&:hover': { backgroundColor: '#8F1E18' } }}
            onClick={() => undoing && void cancel(undoing)}
          />
        </DialogActions>
      </Dialog>
    </Panel>
  );
}
